import { Button, Modal, Table } from 'antd';
import { observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { rootContext } from 'stores';
import { IPrintRecord } from 'types';
import { printDataToSVG } from 'common/svg';
import dayjs from 'dayjs';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { TablePaginationConfig } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';
import { sortOrderType } from 'common/utils';
import SvgRenderer from 'components/Svgrendere/Svgrendere';
import './Userprinthistory.scss';
import { v4 as uuidv4 } from 'uuid';

export type IUserprinthistoryProps = {
  id: string;
};

const Userprinthistory: React.FC<IUserprinthistoryProps> = observer(
  ({ id }) => {
    const [isSvgModalVisible, setIsSvgModalVisible] = useState<string | null>(
      null
    );

    const [svgData, setSvgData] = useState('');
    const [svgModal, setSvgModal] = useState<string | null>(null);

    const {
      UsersStore: {
        getUsersPrintHistory,
        setState,
        state: {
          ui,
          data,
          data: { printHistoryParams, userDetail }
        }
      }
    } = useContext(rootContext);

    const { limit, offset } = printHistoryParams;

    useEffect(() => {
      getUsersPrintHistory({ user_id: id, ...printHistoryParams });
    }, [getUsersPrintHistory, id, printHistoryParams]);

    const onChange = (pagination: any, _: any, sort: any) => {
      const sortInfo = sort as SorterResult<IPrintRecord>;
      setState({
        data: {
          ...data,
          printHistoryParams: {
            ...printHistoryParams,
            limit: pagination.pageSize || printHistoryParams.limit,
            offset: ((pagination.current || 1) - 1) * pagination.pageSize,
            ...(sortInfo.columnKey && sortInfo.order
              ? {
                  sortField: sortInfo.columnKey,
                  sortBy: sortOrderType(sortInfo.order)
                }
              : {})
          }
        }
      });
    };

    return (
      <div className="UserprinthistoryStyles">
        <Table<IPrintRecord>
          title={() => 'Print History'}
          className="Table_block_main topSpace"
          dataSource={userDetail.printHistory || []}
          loading={ui.getPrintHistory}
          pagination={{
            current: Number((offset / limit).toFixed(0)) + 1,
            pageSize: limit,
            total: userDetail.print_total_count,
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '20']
          }}
          onChange={onChange}
          columns={[
            {
              title: 'Part Name',
              align: 'center',
              render: (_, v) => `${v.partData.name} `
            },
            {
              title: 'Brand',
              align: 'center',
              dataIndex: ['partData', 'Brand', 'brand']
            },
            {
              title: 'Page Size',
              align: 'center',
              dataIndex: 'page_size',
              render: (v: number) => v.toFixed(2)
            },
            {
              title: 'Credit',
              dataIndex: 'credit',
              align: 'center',
              render: (v: number) => v.toFixed(2)
            },
            {
              title: 'Printed At',
              dataIndex: 'printed_at',
              align: 'center',
              render: (v: string) => {
                return dayjs(parseInt(v)).format('DD/MM/YYYY HH:mm');
              }
            },
            {
              title: 'Is Edge Wrapped',
              dataIndex: 'is_edge_wrapped',
              align: 'center',
              render: (v: boolean) =>
                v ? <CheckCircleOutlined /> : <CloseCircleOutlined />
            },
            {
              title: 'Pattern Type',
              align: 'center',
              dataIndex: ['partData', 'pattern_type'],
              key: 'pattern_type'
            },
            {
              title: 'Original Part',
              align: 'center',
              render: (value, row) =>
                row.partData.url ? (
                  <SvgRenderer
                    svgUrl={row.partData.url}
                    id={uuidv4()}
                    title="View Part"
                  />
                ) : (
                  'No File'
                )
            },
            {
              title: 'Printed Part',
              align: 'center',
              dataIndex: 'drawing',
              render: v => {
                return (
                  <SvgRenderer
                    svgUrl={printDataToSVG(v).outerHTML}
                    id={uuidv4()}
                    title="View Printed Part"
                  />
                );
              }
            },
            {
              title: 'Compare',
              align: 'center',
              render: (v, row) => {
                return (
                  <SvgRenderer
                    svgUrl={[
                      row?.partData?.url,
                      printDataToSVG(row?.drawing).outerHTML
                    ]}
                    id={uuidv4()}
                    title="Compare Prints"
                  />
                  // <Button
                  //   onClick={async () => {
                  //     if (row.partData.url) {
                  //       const data = await fetch(row.partData.url);
                  //       const textdata = await data.text();
                  //       setSvgData(textdata);
                  //       //  const Svg = printDataToSVG(row.svg_file);
                  //       // const wrapper = document.createElement('div');
                  //       // wrapper.appendChild(Svg);
                  //       // setSvgModal(wrapper.innerHTML);
                  //     }
                  //   }}
                  // >
                  //   Compare Prints
                  // </Button>
                );
              }
            }
          ]}
        />

        <Modal
          visible={Boolean(isSvgModalVisible)}
          footer={false}
          centered={true}
          title="Printed Part"
          destroyOnClose
          width="200vh"
          className="svgmodal"
          onCancel={() => {
            setIsSvgModalVisible(null);
          }}
        >
          <div style={{ height: '70vh', width: '100%' }}>
            {isSvgModalVisible ? (
              <div
                data-zoom-on-wheel
                data-pan-on-drag
                className="svgwrapper"
                dangerouslySetInnerHTML={{ __html: isSvgModalVisible }}
              />
            ) : (
              ''
            )}
          </div>
        </Modal>
        <Modal
          visible={Boolean(svgModal)}
          footer={false}
          centered={true}
          title="Printed Part Comparison Preview"
          destroyOnClose
          width="200vh"
          className="svgmodal"
          onCancel={() => {
            setSvgModal(null);
          }}
        >
          <div className="flex-container">
            {svgModal && (
              <div
                data-zoom-on-wheel
                data-pan-on-drag
                className="svgwrapper"
                dangerouslySetInnerHTML={{ __html: svgModal }}
              />
            )}

            <div
              data-zoom-on-wheel
              data-pan-on-drag
              className="svgwrapper"
              dangerouslySetInnerHTML={{ __html: svgData }}
            />
          </div>
        </Modal>
      </div>
    );
  }
);

export default Userprinthistory;
